/** @jsxImportSource @emotion/react */
import 'twin.macro';

import { numberNoArrow } from 'components/shared/twin.styles';
import { ceil, toNumber } from 'lodash';
import { useEffect, useState } from 'react';
import { Keys } from 'utils/enum';

const PAGE_SIZE_OPTIONS = [12, 30, 60, 120];

type Props = {
  totalItems: number;
  pageSize: number;
  page: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
};

export const UsageTrackingPagination = ({
  totalItems,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
}: Props) => {
  const [pageInput, setPageInput] = useState<string>(page.toString());

  const totalPages = ceil(totalItems / pageSize) || 1;

  // Sync pageInput with page
  useEffect(() => {
    setPageInput(page.toString());
  }, [page]);

  const getPossiblePage = (num: number | string) => {
    const pageNum = toNumber(num);
    if (pageNum >= totalPages) {
      return totalPages;
    }
    if (pageNum < 1) {
      return 1;
    }
    return pageNum;
  };

  const handlePrevPage = () => {
    const newPage = Math.max(page - 1, 1);
    onPageChange(newPage);
  };

  const handleNextPage = () => {
    const newPage = Math.min(page + 1, totalPages);
    onPageChange(newPage);
  };

  const handleGotoPage = () => {
    const newPage = getPossiblePage(pageInput);
    if (newPage !== page) {
      onPageChange(newPage);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === Keys.ENTER || e.key === Keys.ENTER_NUMPAD) {
      handleGotoPage();
    }
  };

  const handleChangePageSize = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPageSize = Number(e.target.value);
    const newTotalPage = ceil(totalItems / newPageSize);
    let newPageNumber = toNumber(pageInput);

    if (newPageNumber > newTotalPage) {
      newPageNumber = newTotalPage;
    }
    if (newPageNumber < 1) {
      newPageNumber = 1;
    }

    onPageSizeChange(newPageSize);
    onPageChange(newPageNumber);
  };

  if (totalItems === 0) {
    return null;
  }

  return (
    <div tw="flex flex-row items-center justify-center gap-4 mt-4 flex-wrap md-down:(gap-3)">
      <div>
        <button
          tw="border-0 h-16 w-44 rounded-md my-12 background[#5551ff] text-xl hover:background[#5551ff] md-down:(h-12 w-32 text-base my-6) sm-down:(h-10 w-24 text-sm my-4)"
          className="button"
          onClick={handlePrevPage}
          disabled={page <= 1}
        >
          Previous
        </button>
      </div>
      <div tw="flex items-center md-down:(order-last w-full justify-center)">
        <input
          tw="width[5.5rem]! text-center outline-none mr-2 mb-0! md-down:(width[4rem]!) sm-down:(width[3.5rem]!)"
          css={numberNoArrow}
          type="number"
          value={pageInput || 1}
          onKeyDown={handleKeyDown}
          onChange={(e) => setPageInput(e.target.value)}
          min={1}
          max={totalPages}
        />
        <div tw="whitespace-nowrap md-down:text-sm">of {totalPages}</div>
      </div>
      <div>
        <button
          tw="border-0 h-16 w-44 rounded-md my-12 background[#5551ff] text-xl hover:background[#5551ff] md-down:(h-12 w-32 text-base my-6) sm-down:(h-10 w-24 text-sm my-4)"
          className="button"
          onClick={handleNextPage}
          disabled={page >= totalPages}
        >
          Next
        </button>
      </div>
      <div tw="flex items-center md-down:(w-full justify-center)">
        <span tw="mr-2.5 md-down:text-sm">Show</span>
        <select
          tw="h-16 w-32 my-12 md-down:(h-12 w-24 my-6) sm-down:(h-10 w-20 my-4)"
          onChange={handleChangePageSize}
          value={pageSize}
        >
          {PAGE_SIZE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

